import {
  Button,
  Inline,
  notifyError,
  notifySuccess,
} from '@omnipresentgroup/design-system';
import {
  useDeleteExpenseMutation,
  useExpensesConfig,
  useGetExpense,
  useUpdateExpenseMutation,
} from '@omnipresentgroup/domains';
import {
  ConfirmationModal,
  useEmployeePermissions,
} from '@omnipresentgroup/shared';
import {
  ExpenseStatusEnum,
  ExpensesEditingMethod,
  isExpenseArchived,
  isExpensePaid,
  isExpensePending,
} from '@omnipresentgroup/types';
import { useMemo, useState } from 'react';

import { environment } from '../../../../environment';
import {
  type ExpenseStatusUpdate,
  ExpenseStatusUpdateModal,
} from './ExpenseStatusUpdateModal';

const MODAL_DESCRIPTION =
  'Are you sure you want to delete this expense? This action cannot be undone.';

export const ExpenseActions = ({ expenseId }: { expenseId: string }) => {
  const [modalInfo, setModalInfo] = useState<{
    open: boolean;
    updateMode?: ExpenseStatusUpdate;
  }>({
    open: false,
  });

  const [isDeletionModalOpen, setIsDeletionModalOpen] =
    useState<boolean>(false);

  const { data: expensesConfig } = useExpensesConfig();

  const { data: expense, isLoading: isExpenseLoading } = useGetExpense({
    expenseId,
  });

  const { mutate: deleteExpense, isPending: isDeleting } =
    useDeleteExpenseMutation({
      onSuccess: () => {
        setIsDeletionModalOpen(false);
        notifySuccess({
          title: 'Success',
          description: `You successfully deleted the expense ${expense?.title}`,
        });
      },
    });

  const { permissions } = useEmployeePermissions({
    employeeId: expense?.employeeId,
    resourceType: 'expense',
    omnipresentTenantId: environment.omnipresentTenantId,
  });

  const canChangeExpense = useMemo(
    () =>
      isExpensePending(expense) &&
      expensesConfig?.editingMethod === ExpensesEditingMethod.MANUAL,
    [expense, expensesConfig?.editingMethod],
  );

  const canDeleteExpense = useMemo(() => {
    if (expensesConfig?.editingMethod !== ExpensesEditingMethod.MANUAL) {
      return false;
    }

    if (!permissions.canDelete) {
      return false;
    }

    if (isExpenseArchived(expense)) {
      return false;
    }

    if (isExpensePaid(expense)) {
      return permissions.canDeleteReported;
    }

    return true;
  }, [
    expensesConfig?.editingMethod,
    permissions.canDelete,
    permissions.canDeleteReported,
    expense,
  ]);

  const { mutate: updateExpense, isPending: isUpdateLoading } =
    useUpdateExpenseMutation({
      onError: () => {
        notifyError({
          title: 'Error',
          description: `Something went wrong while ${
            modalInfo.updateMode ? 'updating' : 'approving'
          } expense ${expense?.title}`,
        });
      },
      onSuccess: () => {
        notifySuccess({
          title: 'Success',
          description: `You successfully ${
            modalInfo.updateMode ? 'updated' : 'approved'
          } the expense ${expense?.title}`,
        });
        setModalInfo({ open: false, updateMode: undefined });
      },
    });

  const reviewButtons = [
    ...(canDeleteExpense
      ? [
          <Button
            key="delete-expense"
            variant="secondary"
            color="negative"
            onClick={() => setIsDeletionModalOpen(true)}
          >
            Delete
          </Button>,
        ]
      : []),
    ...(canChangeExpense
      ? [
          <Button
            key={ExpenseStatusEnum.REJECTED}
            variant="secondary"
            color="negative"
            onClick={() =>
              setModalInfo({
                open: true,
                updateMode: ExpenseStatusEnum.REJECTED,
              })
            }
            loading={
              modalInfo.updateMode === ExpenseStatusEnum.REJECTED &&
              isUpdateLoading
            }
          >
            Reject
          </Button>,
          <Button
            key={ExpenseStatusEnum.INFO_NEEDED}
            variant="secondary"
            onClick={() =>
              setModalInfo({
                open: true,
                updateMode: ExpenseStatusEnum.INFO_NEEDED,
              })
            }
            loading={
              modalInfo.updateMode === ExpenseStatusEnum.INFO_NEEDED &&
              isUpdateLoading
            }
          >
            Request changes
          </Button>,
          <Button
            key={ExpenseStatusEnum.APPROVED}
            loading={isUpdateLoading && modalInfo.updateMode === undefined}
            onClick={() =>
              expense &&
              updateExpense({ id: expense.id, payload: { approved: true } })
            }
            disabled={!isExpensePending || isExpenseLoading}
          >
            Approve
          </Button>,
        ]
      : []),
  ];
  return (
    <>
      <Inline css={{ flexWrap: 'wrap' }}>{reviewButtons}</Inline>
      {expense && modalInfo.open && modalInfo.updateMode && (
        <ExpenseStatusUpdateModal
          modalProps={{
            open: modalInfo.open,
          }}
          updateMode={modalInfo.updateMode}
          isLoading={isUpdateLoading}
          handleSubmit={(reason) => {
            if (modalInfo.updateMode === ExpenseStatusEnum.INFO_NEEDED) {
              updateExpense({
                id: expense.id,
                payload: { requestedChange: reason },
              });
            }
            if (modalInfo.updateMode === ExpenseStatusEnum.REJECTED) {
              updateExpense({
                id: expense.id,
                payload: { rejectionReason: reason },
              });
            }
          }}
          handleCancel={() => {
            setModalInfo({ open: false, updateMode: undefined });
          }}
        />
      )}
      {isDeletionModalOpen && (
        <ConfirmationModal
          modalProps={{
            open: isDeletionModalOpen,
            onOpenChange: setIsDeletionModalOpen,
          }}
          handleConfirm={() => deleteExpense(expenseId)}
          handleCancel={() => setIsDeletionModalOpen(false)}
          loading={isDeleting}
          description={MODAL_DESCRIPTION}
        />
      )}
    </>
  );
};
